import React, { useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import { CompIcon } from 'common/react/components/CompIcon';
import { getTranslation } from 'common/react/utils/translations';
import { SvgIcon } from 'common/react/components/SvgIcon';
import { useBettingVis } from 'common/react/hooks/use-betting-vis';

import { FixtureTeam } from './FixtureTeam';
import { FixtureScore } from './FixtureScore';
import { FixtureVenue } from './FixtureVenue';
import { FixturePosition } from './FixturePosition';

import { useGetLadderQuery } from 'store/modules/api/afl-api';
import { FixtureSummary } from './FixtureSummary';

import { getLadderPosition } from '../../utils/get-ladder-position';
import { FixtureBettingOdds } from './FixtureBettingOdds';
import { FixtureFreeEntry } from './FixtureFreeEntry';
import { FixturePrematchLabel } from './FixturePrematchLabel';
import { FixtureSoldOut } from './FixtureSoldOut';
import { FixtureCountContext } from '../fixtures-list/FixturesList';

const getConferenceLabel = (match, isAway = false) => {
    const conference = isAway
        ? match?.away?.conference
        : match?.home?.conference;

    if (conference?.toUpperCase().includes('CONFERENCE')) {
        return conference.replace(/\s+/g, '-').toLowerCase();
    }

    return null;
};

export const FixtureDetails = ({
    match,
    specialRound,
    liveMatch,
    link,
    matchStatus
}) => {
    const comp = PULSE.app.common.competition.getComp(
        match?.compSeason?.providerId
    );
    const compAbbreviation =
        comp.logoCode?.toLowerCase() ?? comp.code.toLowerCase();

    const home = useMemo(() => ({ ...match?.home }), []);
    const away = useMemo(() => ({ ...match?.away }), []);
    const hasFreeEntry = match?.metadata?.free_entry === 'true';
    const isSoldOut = match?.metadata?.sold_out === 'true';
    const prematchLabel = match?.metadata?.prematch_label ?? '';
    const finalsMatchLabel = match?.metadata?.finals_match_label ?? '';

    const isGFRound = match?.round?.abbreviation === 'GF';

    const isLive = matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE;

    if (liveMatch?.score) {
        home.liveScore = liveMatch.score.homeTeamScore?.matchScore?.totalScore;
        away.liveScore = liveMatch.score.awayTeamScore?.matchScore?.totalScore;
    }

    const { ladderData } = useGetLadderQuery(
        {
            compSeasonId: match.compSeason.id
        },
        {
            selectFromResult: ({ data }) => ({
                ladderData: data?.ladders
            }),
            skip:
                !match ||
                !PULSE.app.common.match.shouldShowPosition(matchStatus)
        }
    );

    const homePosition = useMemo(() => {
        if (ladderData && ladderData[0].entries) {
            return getLadderPosition(
                ladderData[0].entries,
                home.team.providerId
            );
        }
    }, [home, ladderData]);

    const awayPosition = useMemo(() => {
        if (ladderData && ladderData[0].entries) {
            return getLadderPosition(
                ladderData[0].entries,
                away.team.providerId
            );
        }
    }, [away, ladderData]);

    const isBettingVisible = useBettingVis();
    const cardsCount = useContext(FixtureCountContext);

    const compLogo = useCallback(() => {
        if (compAbbreviation === 'afl') {
            return (
                <img
                    src={`${PULSE.app.environment.cdn}i/elements/afl-logo.png`}
                    className="icon fixtures__match-score-divider-icon fixtures__match-score-divider-icon--afl"
                    alt="afl logo"
                />
            );
        }

        return (
            <CompIcon
                providerId={match?.compSeason?.providerId}
                className="icon fixtures__match-score-divider-icon"
            />
        );
    }, [match, compAbbreviation]);

    const compLogoSection = useCallback(() => {
        if (specialRound === 'Gather Round') {
            return (
                <SvgIcon
                    icon="gather-round-flag"
                    className={'fixtures__match-score-divider__gather-round'}
                />
            );
        } else if (specialRound === 'Finals Hub' || isGFRound) {
            return (
                <SvgIcon
                    icon="trophy"
                    className={'fixtures__match-score-divider__finals-hub'}
                />
            );
        }

        return (
            <div className="fixtures__match-score-divider-outline">
                <div
                    className={`fixtures__match-score-divider fixtures__match-score-divider--${compAbbreviation}`}
                >
                    {compLogo()}
                </div>
            </div>
        );
    }, [specialRound, isGFRound, compAbbreviation, compLogo]);

    const bettingOdds = useCallback(() => {
        if (
            matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING &&
            isBettingVisible
        ) {
            return (
                <div className="fixtures__betting-odds">
                    <FixtureBettingOdds match={match} />
                </div>
            );
        }

        return null;
    }, [matchStatus, isBettingVisible]);

    const headerBadges = useCallback(() => {
        const isUpcomingOrTBC =
            matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING ||
            matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.TBC;

        // The finals label shows in every state; the rest only before the match
        const hasBadges =
            finalsMatchLabel ||
            (isUpcomingOrTBC && (prematchLabel || hasFreeEntry || isSoldOut));

        if (!hasBadges) {
            return null;
        }

        return (
            <div className="fixtures__badges-container">
                {isUpcomingOrTBC && prematchLabel && (
                    <FixturePrematchLabel
                        prematchLabel={prematchLabel}
                        isFinals={isGFRound}
                    />
                )}
                {finalsMatchLabel && (
                    <FixturePrematchLabel
                        prematchLabel={finalsMatchLabel}
                        isFinals={isGFRound}
                    />
                )}
                {isUpcomingOrTBC && (
                    <>
                        {hasFreeEntry && (
                            <FixtureFreeEntry competition={compAbbreviation} />
                        )}
                        {isSoldOut && <FixtureSoldOut />}
                    </>
                )}
            </div>
        );
    }, [
        matchStatus,
        hasFreeEntry,
        compAbbreviation,
        isSoldOut,
        prematchLabel,
        finalsMatchLabel,
        isGFRound
    ]);

    const fixtureSummary = useCallback(() => {
        if (
            matchStatus !== PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING ||
            matchStatus !== PULSE.app.common.CONSTANTS.MATCH_STATUS.TBC
        ) {
            return (
                <FixtureSummary
                    home={home}
                    away={away}
                    isLive={isLive}
                    matchStatus={matchStatus}
                />
            );
        }

        return null;
    }, [matchStatus, home, away, isLive]);

    const theme = useMemo(() => {
        return isLive ? 'light' : '';
    }, [isLive]);

    const canLinkToMC = useCallback(() => {
        return (
            !PULSE.app.common.team.isPlaceholderTeam(home?.team?.providerId) &&
            !PULSE.app.common.team.isPlaceholderTeam(away?.team?.providerId)
        );
    }, [home, away]);

    return (
        <div className="fixtures__details">
            {canLinkToMC() && (
                <a
                    href={link}
                    className="fixtures__absolute-link"
                    aria-label={PULSE.app.common.matchAriaLabel({
                        ...match,
                        home: home,
                        away: away,
                        score: liveMatch?.score,
                        status: matchStatus
                    })}
                    data-title={moment(match?.utcStartTime).format(
                        'dddd MMMM D'
                    )}
                    data-content-type={
                        PULSE.app.common.CONSTANTS.TRACKING.CONTENT_TYPES.MATCH
                    }
                    data-index-position={match?.index}
                    onClick={() => {
                        PULSE.app.tracking.selectFixtureCardHandler(
                            match,
                            getTranslation('label.fixtures.card'),
                            link,
                            cardsCount
                        );
                    }}
                ></a>
            )}

            <div className="fixtures__details-main">
                <div className="fixtures__responsive-team-versus">
                    <span>
                        {home?.team.name} v {away?.team.name}
                    </span>
                </div>

                <div className="fixtures__match-team fixtures__match-team--home">
                    <FixtureTeam
                        team={home?.team}
                        theme={theme}
                        conferenceLabel={getConferenceLabel(match)}
                        isFinals={isGFRound}
                    />
                </div>
                <div className="fixtures__match-scoreboard">
                    {isLive && (
                        <FixtureScore
                            score={liveMatch?.score?.homeTeamScore?.matchScore}
                        />
                    )}

                    {/* This also checks if live match has transitioned to completed */}
                    {matchStatus ===
                        PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED && (
                        <FixtureScore
                            score={
                                liveMatch?.score?.homeTeamScore?.matchScore ??
                                home?.score
                            }
                        />
                    )}

                    {PULSE.app.common.match.shouldShowPosition(matchStatus) && (
                        <FixturePosition position={homePosition} />
                    )}

                    {compLogoSection()}

                    {isLive && (
                        <FixtureScore
                            score={liveMatch?.score?.awayTeamScore?.matchScore}
                        />
                    )}

                    {/* This also checks if live match has transitioned to completed */}
                    {matchStatus ===
                        PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED && (
                        <FixtureScore
                            score={
                                liveMatch?.score?.awayTeamScore?.matchScore ??
                                away?.score
                            }
                        />
                    )}

                    {PULSE.app.common.match.shouldShowPosition(matchStatus) && (
                        <FixturePosition position={awayPosition} />
                    )}
                </div>

                <div className="fixtures__match-team fixtures__match-team--away">
                    <FixtureTeam
                        team={away?.team}
                        theme={theme}
                        conferenceLabel={getConferenceLabel(match, true)}
                        isFinals={isGFRound}
                    />
                </div>
            </div>

            {bettingOdds()}
            {headerBadges()}
            {fixtureSummary()}

            {match.venue && <FixtureVenue venue={match.venue} />}
        </div>
    );
};

FixtureDetails.propTypes = {
    match: PropTypes.object.isRequired,
    specialRound: PropTypes.string,
    liveMatch: PropTypes.object,
    matchStatus: PropTypes.string,
    link: PropTypes.string
};
